import React from "react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAgency } from "@/contexts/AgencyContext";
import { APP_VERSION } from "@/config/version";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, loading, error, isAuthenticated } = useAuth();
  const { agency } = useAgency();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated && !loading) {
      console.log('✅ [LoginPage] Usuário já autenticado via sessão salva, redirecionando para dashboard...');
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  // Mostrar loading enquanto verifica autenticação
  if (loading) {
    return (
      <div className="w-full h-full flex justify-center items-center relative">
        <img src="/login-gradient.png" className="absolute top-0 left-0 w-full h-full -z-10" alt="" />
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
          <p className="text-neutral-50 text-lg">Verificando sessão...</p>
          <button
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              window.location.reload();
            }}
            className="mt-4 text-sm text-neutral-400 hover:text-neutral-50 underline transition-colors"
          >
            Limpar sessão e tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    // Limpar erro do contexto também
    console.log('🚀 [LoginPage] Iniciando processo de login...');

    if (!email || !password) {
      setLoginError("Por favor, preencha todos os campos");
      return;
    }

    const success = await login(email, password);
    console.log('📊 [LoginPage] Resultado do login:', success);
    
    if (success) {
      console.log('✅ [LoginPage] Login bem-sucedido, redirecionando para dashboard...');
      navigate("/");
    }
  };

  return (
    <div className=" w-full h-full flex justify-center items-center  relative ">
      <img src="/login-gradient.png" className="absolute top-0 left-0 w-full h-full -z-10" alt="" />
      <div className="w-full gap-4 flex flex-col  max-w-[425px] text-neutral-50">
        <div className="w-full gap-6 flex flex-col  bg-neutral-600  rounded-md  p-6">
          <img 
            src={agency?.logo_url || "/logo-white.png"} 
            className="h-[57px] max-w-[180px] self-center object-contain" 
            alt={agency?.company_name || "Logo"}
            onError={(e) => {
              e.currentTarget.src = "/logo-white.png";
            }}
          />
          {(loginError || error) && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-md">
              <p className="text-red-300 text-sm">{loginError || error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="form-group flex flex-col gap-2">
              <label htmlFor="user" className="text-sm">
                E-mail
              </label>
              <Input 
                type="email" 
                id="user" 
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group flex flex-col gap-2">
              <label htmlFor="password" className="text-sm">
                Senha
              </label>
              <Input 
                type="password" 
                id="password" 
                placeholder="*************"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              className="mt-4"
              disabled={loading}
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="mt-4 text-sm font-light text-neutral-300 hover:text-neutral-50 transition-colors text-left"
            >
              Esqueceu sua senha?
            </button>
          </form>
        </div>
        <span className="text-[11px] text-neutral-400 text-center">v{APP_VERSION}</span>
      </div>
    </div>
  );
};

export default Login;